import React, { useState } from "react";
import get from "lodash/get";

import {
  ContentType,
  JobStatus,
  JobStatusToEmoji,
  JobStatusToString,
} from "../../constants";
import { deleteJob } from "../../apis/firebase";
import {
  formatDestination,
  formatTime,
  formatDate,
  areYouSure,
  trim,
  formatStatName,
} from "../helpers";
import ContentPreviewCompact from "./ContentPreviewCompact";
import Trash from "../utils/icons/Trash";
import ChevronsDown from "../utils/icons/ChevronsDown";
import ChevronsUp from "../utils/icons/ChevronsUp";
import { closePopUp, showLoadingPopUp } from "../utils/Overlay";
import { useJob } from "../../state/Storage";
import Activity from "../utils/icons/Activity";
import JobSnapshotsGraph from "./JobSnapshotsGraph";

export default function JobPreview({
  job,
  user,
  post,
  children,
  setDeletionError,
  onDelete,
  asLi = true,
}: {
  job: Job;
  user: User;
  post: Post;
  key: any;
  children?: any;
  setDeletionError?: Function;
  onDelete?: Function;
  asLi?: boolean;
}) {
  const [j, unsubscribe] = useJob(job.id, job);
  const [details, viewDetails] = useState(false);
  let destination = user.destinations.find(
    (d) => j !== null && d.id === (j as Job).destinationId
  );
  const jobToPreview = j as Job;

  if (!destination) {
    return null;
  }

  const content: Content[] = (j as Job).content
    .map((cId) => {
      return post.content?.find((c) => c.id === cId) as Content;
    })
    .filter((c) => !!c);

  function render() {
    return (
      <>
        <div className="leading-image fz06">
          {jobToPreview.status === JobStatus.PENDING
            ? formatTime(jobToPreview.date.toDate())
            : JobStatusToEmoji[jobToPreview.status] || ""}
        </div>
        <div>
          <div className="grid" style={{ gridTemplateColumns: "90% 10%" }}>
            <div>
              {!details && (
                <>
                  {formatDestination(destination as Destination, "just-icon")}
                  <small className="ib ml05 op06">
                    {getJobAsText(content)}
                  </small>
                  <small className="ib ml05">{getStats(jobToPreview)}</small>
                </>
              )}
              {details && (
                <div className="grid grid2 mt05">
                  <div className="fz08">
                    {printStatus(jobToPreview)}
                    {postedAt(jobToPreview)}
                    {getStatsExpanded(jobToPreview)}
                    {getPostLink(jobToPreview)}
                    {printCustomBackendStatusCode(jobToPreview)}
                    {pair(
                      "Content",
                      content.map((c, i) => (
                        <ContentPreviewCompact
                          content={c}
                          key={i}
                          prefix=""
                          trimTextTo={20}
                          className="ib comma-separated"
                        />
                      ))
                    )}
                  </div>
                  <div>
                    <JobSnapshotsGraph stats={jobToPreview.stats} />
                  </div>
                </div>
              )}
              {children}
            </div>
            <div>
              <button
                className="reset right mr2"
                onClick={() => viewDetails(!details)}
              >
                {!details ? (
                  <ChevronsDown size={20} />
                ) : (
                  <ChevronsUp size={20} />
                )}
              </button>
              {setDeletionError &&
                onDelete &&
                job.status === JobStatus.PENDING && (
                  <button
                    className="reset right mr1"
                    onClick={areYouSure(async () => {
                      try {
                        showLoadingPopUp(`Deleting...`);
                        unsubscribe();
                        await deleteJob(jobToPreview.id);
                        if (onDelete) await onDelete();
                        closePopUp();
                      } catch (err) {
                        console.error(err);
                        setDeletionError(
                          new Error(
                            "There is a problem deleting your task. Please try again later."
                          )
                        );
                      }
                    }, "You are about to delete this post. Are you sure?")}
                  >
                    <Trash size={18} />
                  </button>
                )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return asLi ? (
    <li key={jobToPreview.id} data-id={jobToPreview.id}>
      {render()}
    </li>
  ) : (
    <div
      key={jobToPreview.id}
      data-id={jobToPreview.id}
      className="job-preview-item"
    >
      {render()}
    </div>
  );
}
function printStatus(j: Job) {
  let value = "";

  if (j.status === JobStatus.SUCCESS) {
    value = "success";
  } else if (j.status !== JobStatus.FAILED && j.error) {
    value = ` (reason: ${j.error})`;
  } else if (j.status !== JobStatus.SUCCESS) {
    value = JobStatusToString[j.status] || "";
  }
  return pair("Status", value);
}
export function getPostLink(j: Job) {
  let link;
  if (get(j, "result.url")) {
    link = (
      <a href={get(j, "result.url")} target="_blank">
        {j.result?.campaign ? "campaign" : "post"}
      </a>
    );
  } else if (get(j, "result.posts")) {
    link = get(j, "result.posts").map((url) => (
      <a href={url} target="_blank" key={url}>
        post
      </a>
    ));
  }
  if (link) {
    return pair("Result", link);
  }
  return "";
}
function printCustomBackendStatusCode(j: Job) {
  if (j.result?.status) {
    return pair("Status code", j.result.status);
  }
}
function postedAt(j: Job) {
  if (j.status === JobStatus.PENDING) {
    return pair("Scheduled for", formatDate(j.date.toDate()));
  }
  return j.status === JobStatus.SUCCESS && j.postedAt
    ? pair("Posted at", formatDate(j.postedAt.toDate()))
    : "";
}
function getJobAsText(content: Content[]) {
  if (!content || content.length === 0) return "";
  const text = content.find((c) => c.type === ContentType.TEXT);
  const numOfItems = content.length > 1 ? ` (${content.length} items)` : "";
  if (text) {
    return trim(text.value, 20) + numOfItems;
  }
  return trim(content[0].meta?.name || "", 20) + numOfItems;
}
function getStats(j: Job) {
  if (j.stats && j.stats.engagements > 0) {
    return (
      <span>
        <span style={{ fontSize: "0.8em" }}>
          <Activity size={12} />
        </span>{" "}
        {j.stats.engagements}
      </span>
    );
  }
}
function getStatsExpanded(j: Job) {
  if (j.stats) {
    return pair(
      "Engagements",
      j.stats.engagements +
        " (" +
        Object.keys(j.stats)
          .filter((key) => key !== "engagements" && key !== "snapshots")
          .map((key) => `${formatStatName(key)}=${j.stats[key]}`, "")
          .join(", ") +
        ")"
    );
  }
  return "";
}
function pair(title: string, value) {
  return (
    <div className="job-preview-pair mb03">
      {title}: <strong>{value}</strong>
    </div>
  );
}
